import React, { useEffect, useRef, useState } from "react";

import { daysName, getDaysArr, getMonthArr, getYearArr } from "../helper";

import styles from "./DesktopDatePicker.module.scss";
import ArrowIcon from "@Icons/ArrowIcon";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

interface Props {
  day: string;
  month: string;
  year: string;
  maxYear?: string;
  minYear?: string;
  top?: boolean;
  bottom?: boolean;
  fixed?: boolean;
  handleSelectDate: ({
    dayState,
    monthState,
    yearState,
  }: {
    dayState: string;
    monthState: string;
    yearState: string;
  }) => void;
  hideDatePicker: (value: any) => void;
  datePickerStyles?: string;
  testIdPrefix?: string;
  type?: string;
}
const DatePickerDesktop: React.FC<Props> = (props) => {
  const {
    day,
    month,
    year,
    maxYear,
    minYear,
    handleSelectDate,
    hideDatePicker,
    top,
    bottom,
    fixed,
    datePickerStyles = "",
    testIdPrefix = "",
    type = "",
  } = props;
  const datePickerRef = useRef<any>(null);
  const [dayState, setDayState] = useState<any>(day);
  const [monthState, setMonthState] = useState<any>(month);
  const [yearState, setYearState] = useState<any>(maxYear && year > maxYear ? maxYear : year);
  const { t } = useTranslationHook();

  const getCurrentMonthDays = getDaysArr(Number(monthState), yearState, maxYear, "", true);
  const getMonthList = getMonthArr(minYear, maxYear, yearState);

  const getFirstDateIndex = new Date(Number(yearState), Number(monthState), 1).getDay();
  const prevDaysArr = getDaysArr(Number(monthState) - 1, yearState, maxYear, "", true).map(
    (it) => ({
      display_name: it,
      name: it,
      disable: true,
      prevMonth: true,
      nextMonth: false,
    }),
  );

  const daysArr = getDaysArr(Number(monthState), yearState, "", "", true).map((it, index) => {
    let validDays;
    if (maxYear) {
      validDays = getCurrentMonthDays.length;
    }
    return {
      display_name: it,
      name: it,
      disable: validDays ? !(index < validDays) : false,
      prevMonth: false,
      nextMonth: false,
    };
  });

  const nextDaysArr = getDaysArr(Number(monthState) + 1, yearState, maxYear, "", true).map(
    (it) => ({
      display_name: it,
      name: it,
      disable: true,
      nextMonth: true,
      prevMonth: false,
    }),
  );
  const lastDateIndex = new Date(Number(yearState), Number(monthState), daysArr.length).getDay();
  if (getFirstDateIndex) {
    daysArr.unshift(...prevDaysArr.slice(0 - getFirstDateIndex));
  }
  if (lastDateIndex < 6) {
    daysArr.push(...nextDaysArr.slice(0, 6 - lastDateIndex));
  }

  const handleClickOutside = (e: any) => {
    if (
      datePickerRef.current &&
      !datePickerRef.current?.contains(e.target) &&
      !e.target.classList?.contains(styles.dateValues)
    ) {
      hideDatePicker(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const monthsArr = getMonthList
    .map((...args) => args[1])
    .map((it, index) => ({
      display_name: getMonthList[index],
      name: it,
    }));
  const yearArr = getYearArr(maxYear, minYear).map((it) => ({
    display_name: it,
    name: it,
  }));

  useEffect(() => {
    const paddedCurrentMonthDays = getCurrentMonthDays.map((it) => it.padStart(2, "0"));
    if (!paddedCurrentMonthDays.includes(dayState?.toString())) {
      const days = getCurrentMonthDays;
      setDayState(days[days.length - 1]);
    }
  }, [daysArr, monthState, yearState]);

  useEffect(() => {
    if (!getMonthList.map((it) => it).includes(getMonthList[monthState])) {
      const months = getMonthList;
      setMonthState((months.length - 1)?.toString());
    }
  }, [yearState]);

  useEffect(() => {
    if (dayState && (monthState || monthState?.toString() === "0") && yearState) {
      handleSelectDate({
        dayState,
        monthState: `${Number(monthState) + 1}`,
        yearState,
      });
    }
  }, [dayState, monthState, yearState]);

  const handleDateSelect = (item: any): void => {
    if (!item.nextMonth || !daysArr.some((it) => it.disable && !it.prevMonth && !it.nextMonth)) {
      if (item.disable) {
        if (item.prevMonth || item.nextMonth) {
          setDayState(item.display_name.padStart(2, "0"));
        }
      } else {
        setDayState(item.display_name.padStart(2, "0"));
      }
      if (item.prevMonth) {
        if (monthState?.toString() !== "0") {
          setMonthState((Number(monthState) - 1)?.toString());
        } else {
          setMonthState("11");
          setYearState((Number(yearState) - 1)?.toString());
        }
      }
      if (item.nextMonth) {
        if (monthState?.toString() !== "11") setMonthState((Number(monthState) + 1)?.toString());
        else {
          setMonthState("0");
          setYearState((Number(yearState) + 1)?.toString());
        }
      }
      if (!item.disable) setTimeout(() => hideDatePicker(false), 0);
    }
  };

  const useSelectOpions = (options: any, value: any, setVal: any) => {
    const [showOption, setOptions] = useState(false);
    const dropDownRef = useRef<any>(null);
    const selectedOption = useRef<any>(null);
    const scrollableAreaRef = useRef<any>(null);
    let offSetTop = 0;
    if (selectedOption.current) offSetTop = selectedOption.current.offsetTop;
    if (scrollableAreaRef.current) scrollableAreaRef.current.scrollTop = offSetTop;

    const handleClick = (e: any) => {
      if (!dropDownRef.current?.contains(e.target)) {
        setOptions(false);
      }
    };
    useEffect(() => {
      window.addEventListener("click", handleClick);
      return () => {
        window.removeEventListener("click", handleClick);
      };
    }, []);

    const handleShowOptions = () => {
      setOptions(!showOption);
    };

    const handleValueSelect = (item: any): void => {
      setOptions(!showOption);
      setVal(item.name);
    };

    return (
      <div>
        <div
          ref={dropDownRef}
          onClick={handleShowOptions}
          className={formatClassNames(styles.options, showOption && styles.optionsOpen)}
          role="none"
        >
          {t(String(value))}
          <div>
            <ArrowIcon />
          </div>
        </div>
        <div
          ref={scrollableAreaRef}
          className={formatClassNames(styles.optionsContainer, showOption && styles.displayOptions)}
        >
          {options.map((item: any) => (
            <div
              ref={value?.toString() === item.display_name?.toString() ? selectedOption : null}
              key={item.name}
              onClick={() => handleValueSelect(item)}
              className={formatClassNames(
                styles.selectOptions,
                value?.toString() === item?.display_name?.toString() && styles.selctedOption,
              )}
              role="none"
            >
              {t(String(item.display_name))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      ref={datePickerRef}
      className={formatClassNames(
        styles.container,
        top && styles.top,
        bottom && styles.bottom,
        fixed && styles.fixed,
        datePickerStyles,
        type && styles[type],
      )}
    >
      <div className={styles.select}>
        <div className={styles.selectOptionContainer}>
          {useSelectOpions(monthsArr, getMonthList[monthState], setMonthState)}
        </div>
        <div className={styles.selectOptionContainer}>
          {useSelectOpions(yearArr, yearState, setYearState)}
        </div>
      </div>
      <div className={styles.days}>
        {daysName.map((item) => (
          <div>{t(item.slice(0, 2))}</div>
        ))}
      </div>
      <div className={formatClassNames(styles.days, styles.daysNumber)}>
        {daysArr.map((item, index) => (
          <div
            key={`${item.display_name} ${index}`}
            onClick={() => handleDateSelect(item)}
            className={formatClassNames(
              item.disable && styles.disableDay,
              styles.dateValues,
              dayState?.toString() === item.display_name.padStart(2, "0") &&
                !item.disable &&
                styles.selected,
              item.disable && !item.prevMonth && !item.nextMonth && styles.disabled,
              item.nextMonth &&
                daysArr.some((it) => it.disable && !it.prevMonth && !it.nextMonth) &&
                styles.disabled,
            )}
            role="none"
            data-testid={`${testIdPrefix}leadform-move-in-dates`}
          >
            {item?.display_name}
          </div>
        ))}
      </div>
    </div>
  );
};

DatePickerDesktop.defaultProps = {
  maxYear: "",
  top: false,
  bottom: false,
  fixed: false,
};

export default wrapperHOC(DatePickerDesktop, {
  componentName: "DatePickerDesktop-DatePicker-Global",
  showForChina: true,
});
